import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import azureDevOpsService from './services/azureDevOpsService';

const AZDO_ORG = process.env.REACT_APP_AZURE_ORG || 'chapsvision';
const AZDO_PROJECT = process.env.REACT_APP_AZURE_PROJECT || 'Fabrique';

const TYPE_FILTERS = [
  { key: 'all', label: 'Tous' },
  { key: 'Product Backlog Item', label: 'PBI' },
  { key: 'Bug', label: 'Bugs' },
];

function isoDay(d) {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().split('T')[0];
}

function daysAgo(n) {
  return isoDay(new Date(Date.now() - n * 24 * 60 * 60 * 1000));
}

const PRESETS = [
  { key: '7d',   label: '7 derniers jours',  range: () => [daysAgo(7), isoDay(new Date())] },
  { key: '30d',  label: '30 derniers jours', range: () => [daysAgo(30), isoDay(new Date())] },
  { key: '90d',  label: '3 derniers mois',   range: () => [daysAgo(90), isoDay(new Date())] },
  { key: 'ytd',  label: 'Depuis le 1er janvier', range: () => [`${new Date().getFullYear()}-01-01`, isoDay(new Date())] },
];

function mean(arr) {
  return arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : null;
}

function median(arr) {
  if (!arr.length) return null;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function fmt(v) {
  return v == null ? '–' : v.toFixed(1);
}

export default function KpiTimeInTest() {
  const [preset, setPreset] = useState('30d');
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(isoDay(new Date()));
  const [sampleSize, setSampleSize] = useState(50);
  const [typeFilter, setTypeFilter] = useState('all');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);

  async function calculate(start = startDate, end = endDate) {
    setLoading(true);
    setError(null);
    setProgress(null);
    try {
      const res = await azureDevOpsService.getTestStateDurations(start, end, sampleSize, (done, total) =>
        setProgress({ done, total })
      );
      setResult({ ...res, startDate: start, endDate: end, sampleSize });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { calculate(); }, []);

  function applyPreset(p) {
    const [s, e] = p.range();
    setPreset(p.key);
    setStartDate(s);
    setEndDate(e);
    calculate(s, e);
  }

  const tickets = useMemo(() => {
    const all = result?.tickets ?? [];
    return typeFilter === 'all' ? all : all.filter(t => t.type === typeFilter);
  }, [result, typeFilter]);

  const durations = tickets.map(t => t.days);

  const bySquad = useMemo(() => {
    const groups = {};
    for (const t of tickets) (groups[t.squad] = groups[t.squad] || []).push(t.days);
    return Object.entries(groups)
      .map(([squad, days]) => ({
        squad,
        count: days.length,
        average: Math.round(mean(days) * 10) / 10,
        median: median(days),
        max: Math.max(...days),
      }))
      .sort((a, b) => {
        if (a.squad === 'Autres') return 1;
        if (b.squad === 'Autres') return -1;
        return b.average - a.average;
      });
  }, [tickets]);

  const longest = useMemo(() => [...tickets].sort((a, b) => b.days - a.days).slice(0, 10), [tickets]);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Temps moyen passé en Test</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          PBI et Bugs · Tribu Argonos · temps cumulé dans l'état 3- Test (jours calendaires),
          sur un échantillon de tickets arrivés en 4- Doc ou au-delà pendant la période
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-col gap-3 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map(p => (
            <button
              key={p.key}
              onClick={() => applyPreset(p)}
              disabled={loading}
              className={'px-3 py-1.5 rounded-md text-sm font-medium border transition-colors disabled:opacity-50 ' +
                (preset === p.key
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100')}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Date début
            </label>
            <input
              type="date"
              value={startDate}
              onChange={e => { setStartDate(e.target.value); setPreset(null); }}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Date fin
            </label>
            <input
              type="date"
              value={endDate}
              onChange={e => { setEndDate(e.target.value); setPreset(null); }}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Échantillon <span className="font-normal normal-case text-gray-400">(min 50 tickets)</span>
            </label>
            <input
              type="number"
              value={sampleSize}
              min="50"
              max="500"
              step="10"
              onChange={e => setSampleSize(Math.max(50, Math.min(500, Number(e.target.value) || 50)))}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-24 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => calculate()}
            disabled={loading || !startDate || !endDate || startDate > endDate}
            className="px-5 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm"
          >
            {loading ? 'Calcul…' : 'Calculer'}
          </button>
          <div className="ml-auto flex rounded-md border border-gray-300 overflow-hidden">
            {TYPE_FILTERS.map(f => (
              <button
                key={f.key}
                onClick={() => setTypeFilter(f.key)}
                className={'px-3 py-1.5 text-sm font-medium transition-colors ' +
                  (typeFilter === f.key ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-100')}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
          Erreur : {error}
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center h-36 text-gray-400 gap-3">
          <svg className="animate-spin h-7 w-7 text-blue-500" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm">
            {progress
              ? `Lecture de l'historique des tickets… ${progress.done} / ${progress.total}`
              : 'Recherche des tickets passés en Test…'}
          </span>
        </div>
      )}

      {result && !loading && (
        <>
          {result.candidates > 0 && result.candidates < 50 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2 rounded-lg mb-4 text-sm">
              Seulement {result.candidates} tickets sur cette période : en dessous de 50, la moyenne est peu
              représentative. Élargissez la période.
            </div>
          )}
          {result.failed > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2 rounded-lg mb-4 text-sm">
              L'historique de {result.failed} tickets n'a pas pu être lu ; ils sont exclus du calcul.
            </div>
          )}

          {/* KPI cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-5 rounded-xl border-2 border-blue-200">
              <div className="text-xs font-semibold text-blue-500 uppercase tracking-wide mb-1">
                Temps moyen en Test
              </div>
              <div className="text-5xl font-bold text-blue-700 leading-none">{fmt(mean(durations))}</div>
              <div className="text-sm text-blue-500 mt-2">jours</div>
            </div>
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-5 rounded-xl border-2 border-indigo-200">
              <div className="text-xs font-semibold text-indigo-500 uppercase tracking-wide mb-1">
                Médiane
              </div>
              <div className="text-5xl font-bold text-indigo-700 leading-none">{fmt(median(durations))}</div>
              <div className="text-sm text-indigo-500 mt-2">jours</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-5 rounded-xl border-2 border-green-200">
              <div className="text-xs font-semibold text-green-500 uppercase tracking-wide mb-1">
                Tickets analysés
              </div>
              <div className="text-5xl font-bold text-green-700 leading-none">{tickets.length}</div>
              <div className="text-xs text-green-500 mt-2">
                sur {result.candidates} éligibles · du {result.startDate.split('-').reverse().join('/')} au {result.endDate.split('-').reverse().join('/')}
              </div>
            </div>
          </div>

          {bySquad.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-6">
              Aucun ticket en 4- Doc ou au-delà, passé par Test, sur cette période.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Chart par squad */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Temps moyen en Test par squad (jours)
                </h3>
                <ResponsiveContainer width="100%" height={Math.max(180, bySquad.length * 36)}>
                  <BarChart data={bySquad} layout="vertical" margin={{ top: 0, right: 40, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#6b7280' }} />
                    <YAxis
                      type="category"
                      dataKey="squad"
                      width={170}
                      tick={{ fontSize: 12, fill: '#374151' }}
                      tickFormatter={s => s.replace(/^Squad /, '')}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                      formatter={(v, _n, item) => [`${v} j (${item.payload.count} tickets)`, 'Moyenne']}
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                    />
                    <Bar dataKey="average" fill="#3b82f6" radius={[0, 4, 4, 0]}>
                      <LabelList dataKey="average" position="right" style={{ fontSize: 11, fill: '#374151' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Tableau par squad */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Détail par squad
                </h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-2 font-semibold">Squad</th>
                      <th className="py-2 px-2 font-semibold text-right">Tickets</th>
                      <th className="py-2 px-2 font-semibold text-right">Moyenne</th>
                      <th className="py-2 px-2 font-semibold text-right">Médiane</th>
                      <th className="py-2 pl-2 font-semibold text-right">Max</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bySquad.map(s => (
                      <tr key={s.squad} className="border-b border-gray-100">
                        <td className="py-1.5 pr-2 text-gray-800">{s.squad}</td>
                        <td className="py-1.5 px-2 text-right text-gray-600">{s.count}</td>
                        <td className="py-1.5 px-2 text-right font-semibold text-gray-900">{fmt(s.average)} j</td>
                        <td className="py-1.5 px-2 text-right text-gray-600">{fmt(s.median)} j</td>
                        <td className="py-1.5 pl-2 text-right text-gray-600">{fmt(s.max)} j</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {longest.length > 0 && (
            <details className="mt-6">
              <summary className="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-900">
                Les {longest.length} tickets restés le plus longtemps en Test
              </summary>
              <ul className="mt-3 divide-y divide-gray-100 text-sm">
                {longest.map(t => (
                  <li key={t.id} className="flex items-center gap-3 py-1.5">
                    <span className="w-16 text-right font-semibold text-gray-900">{fmt(t.days)} j</span>
                    <a
                      href={`https://dev.azure.com/${AZDO_ORG}/${AZDO_PROJECT}/_workitems/edit/${t.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      #{t.id}
                    </a>
                    <span className="flex-1 truncate text-gray-700" title={t.title}>{t.title}</span>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {t.type === 'Bug' ? 'Bug' : 'PBI'} · {t.squad.replace(/^Squad /, '')}
                      {t.passages > 1 && ` · ${t.passages} passages`}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}
